import { post } from 'modules/api';
import { isAndroidTWAContext } from 'modules/androidTWA';
import { captureException } from 'modules/error-reporting';

const WEBPUSHNOTIFICATION_SERVER_PUBLIC_KEY = process.env.WEBPUSHNOTIFICATION_SERVER_PUBLIC_KEY;

/**
 * urlBase64ToUint8Array
 *
 * @param {string} base64String a public vavid key
 *
 * cf. https://github.com/GoogleChromeLabs/web-push-codelab/issues/46#issuecomment-429273981
 */
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

const askPermission = () => {
    return new Promise(function(resolve, reject) {
        const permissionResult = Notification.requestPermission(function(result) {
            resolve(result);
        });

        if (permissionResult) {
            permissionResult.then(resolve, reject);
        }
    }).then(function(permissionResult) {
        if (permissionResult !== 'granted') {
            // throw to stop global functionality
            throw new Error("We weren't granted permission for webpush notification.");
        }
    });
};

const postSubscriptionToServer = pushSubscription => {
    const pushSubscriptionForOurAPI = {
        subscription: {
            endpoint: pushSubscription.endpoint,
            keys: pushSubscription.toJSON().keys,
        },
    };
    return post('notification/webpush', pushSubscriptionForOurAPI)
        .then(() => pushSubscription)
        .catch(error => captureException(error));
};

// register the service worker that will handle notifications in browser
// and subscribe the browser with the current user to our notifications
const subscribeUserToPush = () => {
    return navigator.serviceWorker
        .register('/assets/workers/service-worker.js')
        .then(reg => {
            reg.pushManager.getSubscription().then(sub => {
                if (sub === undefined || sub === null) {
                    const subscribeOptions = {
                        userVisibleOnly: true,
                        applicationServerKey: urlBase64ToUint8Array(
                            WEBPUSHNOTIFICATION_SERVER_PUBLIC_KEY
                        ),
                    };
                    reg.pushManager.subscribe(subscribeOptions).then(postSubscriptionToServer);
                } else {
                    postSubscriptionToServer(sub);
                }

                return reg.update();
            });
        })
        .catch(error => captureException(error));
};

const handleWebPushAtStartup = () => {
    // Check that the application context is Android TWA
    if (isAndroidTWAContext()) {
        // Check that the browser supports Service Workers and Push notifications
        if ('serviceWorker' in navigator && 'PushManager' in window) {
            // Ask the user permission to send notifications
            askPermission()
                .then(subscribeUserToPush) // subscribe the user with this browser to our notifications
                .catch(() => {});
        }
    }
};

export { handleWebPushAtStartup };
